import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, X, FileText, ExternalLink, User } from 'lucide-react';
import { format } from 'date-fns';

export default function ArtifactApprovalQueue({ artifacts, onApprove, onReject }) {
  const pendingArtifacts = artifacts.filter(a => a.status === 'pending_approval');

  if (pendingArtifacts.length === 0) return null;

  return (
    <Card className="mb-8 border-amber-500/30 bg-amber-500/5">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-amber-400" />
            <CardTitle className="text-lg font-semibold text-amber-100">
              Artifacts Pending Approval ({pendingArtifacts.length})
            </CardTitle>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {pendingArtifacts.map((artifact) => (
            <div key={artifact.id} className="bg-slate-900/50 border border-slate-800 rounded-lg p-4 flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-medium text-slate-200">{artifact.name}</h3>
                  <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">
                    {artifact.category}
                  </Badge>
                  {artifact.linked_task_id && (
                    <Badge variant="outline" className="text-xs border-indigo-500/30 text-indigo-400">
                      From Task
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-slate-400 mb-2">{artifact.description}</p>
                <div className="flex flex-wrap gap-4 text-xs text-slate-500">
                  <div className="flex items-center gap-1">
                    <User className="w-3 h-3" />
                    {artifact.owner_email}
                  </div>
                  {artifact.created_date && (
                    <span>Created: {format(new Date(artifact.created_date), 'MMM d, yyyy')}</span>
                  )}
                  {artifact.file_url && (
                    <a 
                      href={artifact.file_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-indigo-400 hover:text-indigo-300 flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" />
                      View Artifact
                    </a>
                  )}
                </div>
              </div>
              <div className="flex gap-2 w-full md:w-auto">
                <Button 
                  size="sm" 
                  onClick={() => onApprove(artifact.id)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1 md:flex-none"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => onReject(artifact.id)}
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10 flex-1 md:flex-none"
                >
                  <X className="w-4 h-4 mr-2" />
                  Reject
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}